/**
 * CSRF Protection Service
 * 
 * Implements Cross-Site Request Forgery protection using state parameters
 * and CSRF tokens.
 */

import { CookieOptions, CsrfTokenData, SecurityEvent } from '../types/security.types';
import { generateRandomString } from '../utils/crypto.utils';

/**
 * CSRF token storage (in-memory, will be replaced with session storage)
 */
class CsrfTokenStore {
  private tokens: Map<string, CsrfTokenData> = new Map();

  async get(token: string): Promise<CsrfTokenData | null> {
    const data = this.tokens.get(token);
    if (!data) {
      return null;
    }

    // Check if token has expired
    if (Date.now() > data.expiresAt) {
      this.tokens.delete(token);
      return null;
    }

    return data;
  }

  async set(token: string, data: CsrfTokenData): Promise<void> {
    this.tokens.set(token, data);
  }

  async delete(token: string): Promise<void> {
    this.tokens.delete(token);
  }

  async cleanup(): Promise<void> {
    const now = Date.now();
    for (const [token, data] of this.tokens.entries()) {
      if (now > data.expiresAt) {
        this.tokens.delete(token);
      }
    }
  }
}

/**
 * CSRF Protection Service
 */
export class CsrfService {
  private tokenStore: CsrfTokenStore;
  private securityEvents: SecurityEvent[] = [];
  private readonly TOKEN_EXPIRY_MS = 60 * 60 * 1000; // 1 hour

  constructor() {
    this.tokenStore = new CsrfTokenStore();

    // Cleanup expired tokens every 5 minutes
    setInterval(() => {
      this.tokenStore.cleanup();
    }, 5 * 60 * 1000);
  }

  /**
   * Generate a new CSRF token for a session
   */
  async generateToken(sessionId: string): Promise<string> {
    const token = generateRandomString(32);
    const expiresAt = Date.now() + this.TOKEN_EXPIRY_MS;

    await this.tokenStore.set(token, {
      token,
      sessionId,
      expiresAt,
    });

    return token;
  }

  /**
   * Validate a CSRF token
   */
  async validateToken(token: string, sessionId: string): Promise<boolean> {
    if (!token || !sessionId) {
      await this.logCsrfFailure('missing_token_or_session', { token, sessionId });
      return false;
    }

    const tokenData = await this.tokenStore.get(token);
    if (!tokenData) {
      await this.logCsrfFailure('invalid_token', { token, sessionId });
      return false;
    }

    if (tokenData.sessionId !== sessionId) {
      await this.logCsrfFailure('session_mismatch', {
        token,
        expectedSession: sessionId,
        actualSession: tokenData.sessionId,
      });
      return false;
    }

    return true;
  }

  /**
   * Validate OAuth state parameter (CSRF protection for OAuth flow)
   */
  async validateState(
    receivedState: string,
    expectedState: string
  ): Promise<boolean> {
    if (!receivedState || !expectedState) {
      await this.logCsrfFailure('missing_state', { receivedState, expectedState });
      return false;
    }

    if (receivedState !== expectedState) {
      await this.logCsrfFailure('state_mismatch', { receivedState, expectedState });
      return false;
    }

    return true;
  }

  /**
   * Generate secure cookie options for authentication cookies
   */
  getSecureCookieOptions(): CookieOptions {
    return {
      httpOnly: true,
      secure: true,
      sameSite: 'Strict',
      path: '/',
    };
  }

  /**
   * Validate cookie security attributes
   */
  validateCookieOptions(options: CookieOptions): boolean {
    return (
      options.httpOnly === true &&
      options.secure === true &&
      options.sameSite === 'Strict'
    );
  }

  /**
   * Invalidate a CSRF token (e.g., after use or logout)
   */
  async invalidateToken(token: string): Promise<void> {
    await this.tokenStore.delete(token);
  }

  /**
   * Log CSRF validation failure
   */
  private async logCsrfFailure(
    reason: string,
    details: Record<string, any>
  ): Promise<void> {
    const event: SecurityEvent = {
      type: 'csrf_failure',
      timestamp: new Date().toISOString(),
      ipAddress: 'unknown', // Will be populated by middleware
      details: {
        reason,
        ...details,
      },
    };

    this.securityEvents.push(event);

    // In production, this would send to CloudWatch Logs or audit service
    console.warn('[SECURITY] CSRF validation failed:', event);
  }

  /**
   * Get security events (for testing/monitoring)
   */
  getSecurityEvents(): SecurityEvent[] {
    return [...this.securityEvents];
  }

  /**
   * Clear security events (for testing)
   */
  clearSecurityEvents(): void {
    this.securityEvents = [];
  }
}

// Export singleton instance
export const csrfService = new CsrfService();
